import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { channelName } from '../icons'
import { useToast } from '../ui/Toaster'

/**
 * Configurações → Catálogo. Os anúncios que o SAC conhece em cada canal — é daqui que sai o
 * anúncio enviado no "Enviar" da lateral de produtos. Atualiza sozinho uma vez por semana; o
 * botão é para quando acabou de anunciar algo e não quer esperar.
 */

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

export function CatalogTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: api.catalog,
    // Enquanto roda, acompanha; parado, não precisa.
    refetchInterval: (q) => (q.state.data?.sync.running ? 5000 : false),
  })
  const sync = useMutation({
    mutationFn: api.catalogSync,
    onSuccess: () => {
      toast('Atualização iniciada. Leva alguns minutos.')
      void qc.invalidateQueries({ queryKey: ['catalog'] })
    },
    onError: (e: Error) => toast(e instanceof ApiError && e.status === 409 ? 'Já há uma atualização em andamento.' : 'Não consegui iniciar a atualização.'),
  })

  if (isLoading || !data) return <div className="empty">Carregando…</div>
  const s = data.sync
  const erros = s.results.flatMap((r) => r.errors.map((e) => `${r.label}: ${e}`))

  return (
    <>
      <div className="crm-head">
        <h2>Catálogo</h2>
        <p>
          Os anúncios que o SAC conhece em cada canal. É daqui que sai o anúncio enviado pelo “Enviar” da lateral de
          produtos e o link que aparece no título. Atualiza sozinho uma vez por semana.
        </p>
      </div>

      <div className="cfg-block">
        <div className="cfg-row">
          <div style={{ flex: 1 }}>
            <b>{s.running ? 'Atualizando agora…' : `Última atualização completa: ${when(data.lastCompletedAt)}`}</b>
            <div className="cfg-note">
              {s.running
                ? `Começou às ${when(s.startedAt)}. Pode sair desta tela; ela continua.`
                : data.nextAutoAt
                  ? `Próxima automática: ${when(data.nextAutoAt)}.`
                  : 'A automática roda na próxima hora.'}
            </div>
          </div>
          <button className="btn pri sm" disabled={s.running || sync.isPending} onClick={() => sync.mutate()}>
            Atualizar agora
          </button>
        </div>
        {s.error && <div className="cfg-note danger">A última atualização parou com erro: {s.error}</div>}
        {erros.length > 0 && (
          <div className="cfg-note danger">
            {erros.slice(0, 5).map((e) => <div key={e}>{e}</div>)}
          </div>
        )}
      </div>

      <div className="cfg-block">
        <div className="blk-h">Anúncios por canal</div>
        {data.channels.map((c) => (
          <div className="cfg-row" key={c.channel_type}>
            <div style={{ flex: 1 }}>
              <b>{c.channel_type === 'mercadolivre_question' ? 'Mercado Livre (perguntas)' : channelName(c.channel_type as never)}</b>
              <div className="cfg-note">
                {c.listings.toLocaleString('pt-BR')} anúncios · {c.skus.toLocaleString('pt-BR')} SKUs · {c.active.toLocaleString('pt-BR')} ativos
              </div>
            </div>
            <span className="cfg-note">atualizado em {when(c.last_synced)}</span>
          </div>
        ))}
      </div>
    </>
  )
}
